"use client"

import truncateQuery from "@/utils/truncateQuery"
import { Dialog, DialogContent, DialogTrigger } from "components/ui/dialog"
import { Button } from "components/ui/button"
import { Edit3, FileText, Plus } from "lucide-react"
import { useState, useEffect } from "react"

interface TextAreaModalProps {
    value: string
    onChange: (value: string) => void
    onBlur: () => void
    title: string
}

export const TextAreaModal = ({ value, onChange, onBlur, title }: TextAreaModalProps) => {
    const [localValue, setLocalValue] = useState(value)
    const [isOpen, setIsOpen] = useState(false)
    const hasContent = value && value.trim().length > 0

    useEffect(() => {
        setLocalValue(value)
    }, [value])

    const handleClose = () => {
        onChange(localValue)
        onBlur()
        setIsOpen(false)
    }

    const handleSave = () => {
        handleClose()
    }

    return (
        <Dialog open={isOpen} onOpenChange={(open) => {
            if (!open) {
                handleClose()
            } else {
                setIsOpen(true)
            }
        }}>
            <DialogTrigger asChild>
                <button
                    className="w-full p-3 border border-gray-200 rounded-lg hover:border-gray-300 transition-colors group bg-white md:p-2 md:border-0 md:bg-transparent md:hover:bg-gray-50 md:rounded-md md:flex md:items-center md:justify-start md:gap-2 h-full"
                    onClick={() => setIsOpen(true)}
                >
                    {hasContent ? (
                        <>
                            {/* Layout Mobile - Card vertical */}
                            <div className="space-y-2 md:hidden">
                                {/* Preview do texto */}
                                <div className="text-left">
                                    <p className="text-sm text-gray-700 break-words overflow-hidden"
                                        style={{
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical'
                                        }}>
                                        {value}
                                    </p>
                                </div>
                                <div className="flex items-center justify-center gap-2 pt-2 border-t border-gray-100">
                                    <Edit3 className="h-4 w-4 text-blue-600" />
                                    <span className="text-sm font-medium text-blue-600">
                                        Editar Justificativa
                                    </span>
                                </div>
                            </div>

                            {/* Layout Desktop - Inline horizontal */}
                            <div className="hidden md:flex md:items-center md:gap-2 md:w-full">
                                {/* <FileText className="h-4 w-4 text-gray-500 group-hover:text-[#4B303C] transition-colors flex-shrink-0" /> */}
                                <span className="text-sm text-gray-700 truncate flex-1">
                                    {truncateQuery(value, 50)}
                                </span>
                                <div className="flex items-center gap-1 text-blue-600 group-hover:text-blue-700 flex-shrink-0">
                                    <Edit3 className="h-3 w-3" />
                                    <span className="text-sm font-medium">Editar</span>
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Layout Mobile - Estado vazio */}
                            <div className="flex flex-col items-center justify-center gap-2 py-4 md:hidden">
                                <Plus className="h-6 w-6 text-gray-400" />
                                <span className="text-sm text-gray-500 font-medium">
                                    Clique para adicionar sua justificativa
                                </span>
                                <span className="text-xs text-gray-400">
                                    Campo obrigatório
                                </span>
                            </div>

                            {/* Layout Desktop - Estado vazio inline */}
                            <div className="hidden md:flex md:items-center md:gap-2">
                                <Plus className="h-4 w-4 text-gray-500 group-hover:text-[#4B303C] transition-colors" />
                                <span className="text-sm text-gray-500 group-hover:text-[#4B303C] transition-colors">
                                    Clique para adicionar texto
                                </span>
                            </div>
                        </>
                    )}
                </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto pb-0">
                <div className="flex items-center justify-between pb-4 border-b">
                    <h2 className="text-lg font-semibold flex items-center gap-2">
                        <FileText className="h-5 w-5 text-[#2B0C1A]" />
                        {title}
                    </h2>
                </div>

                <div className="space-y-4 pb-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                        <p className="text-sm text-yellow-800">
                            💡 <strong>Dica:</strong> Explique detalhadamente o motivo da divergência para facilitar a análise do time financeiro.
                        </p>
                    </div>

                    <textarea
                        value={localValue}
                        onChange={(e) => setLocalValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                                e.preventDefault()
                                handleSave()
                            }
                        }}
                        className="w-full min-h-[200px] p-4 border border-gray-300 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-[#2B0C1A] focus:border-transparent text-sm"
                        placeholder="Digite sua justificativa aqui...

Exemplo: 
• Problema no sistema no dia X
• Troco foi dado em dinheiro mas não registrado
• Diferença devido a..."
                        autoFocus
                    />

                    <div className="flex justify-between items-center text-xs text-gray-500 bg-gray-50 p-2 rounded">
                        <span className="flex items-center gap-1">
                            📝 {localValue.length} caracteres
                        </span>
                        <div className="flex gap-2 text-xs">
                            <span className="hidden sm:block">Ctrl+Enter para salvar</span>
                            <span className="hidden sm:block">•</span>
                            <span className="hidden sm:block">Esc para cancelar</span>
                        </div>
                    </div>

                    <div className="flex justify-end pt-2">
                        <Button
                            onClick={handleSave}
                            className="bg-[#2B0C1A] text-white hover:bg-[#2B0C1A]/85 flex gap-2"
                        >
                            Salvar
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    )
}